/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import VerifiedIcon from './VerifiedIcon'
import {
  profileLinkStyles,
  avatarContainerStyles,
  profileInfoStyles,
  getAvatarStyles,
  getUserProfileStyles,
} from './FeedItem.styles'
import {
  EVERYONE_COLORS,
  ROLE_GROUPS,
  TOTAL_ROLE_COUNT,
  getRoleColors,
} from '@sportninja/common/constants/roleGroups'

const badgeStyles = css`
  padding: 0px 4px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`

interface RoleBadgesProps {
  role_types: { id: number; name: string }[]
}

const PLURAL_MAP: Record<string, string> = {
  Owner: 'Owners',
  Admin: 'Admins',
  'League Manager': 'League Managers',
  Referee: 'Referees',
  Scorekeeper: 'Scorekeepers',
  Videographer: 'Videographers',
  Player: 'Players',
  'Team Official': 'Team Officials',
  Timekeeper: 'Timekeepers',
  Following: 'Followers',
}

/**
 * Smart role badges display:
 * - All 10 roles → single "ALL" badge
 * - Full group → single group badge with tooltip
 * - Otherwise → individual badges
 */
export const RoleBadges: React.FC<RoleBadgesProps> = ({ role_types }) => {
  const badges = useMemo(() => {
    if (!role_types || role_types.length === 0) {
      return null
    }

    const roleIds = new Set(role_types.map((r) => r.id))

    // Check if ALL roles are present
    if (roleIds.size >= TOTAL_ROLE_COUNT) {
      return (
        <span
          css={[
            badgeStyles,
            css`
              background: ${EVERYONE_COLORS.bg};
              color: ${EVERYONE_COLORS.fg};
            `,
          ]}
        >
          Everyone
        </span>
      )
    }

    const elements: React.ReactNode[] = []
    const coveredRoleIds = new Set<number>()

    // Check each group
    for (const group of ROLE_GROUPS) {
      const groupRoleIds = group.roles.map((r) => parseInt(r.id, 10))
      const allInGroup = groupRoleIds.every((id) => roleIds.has(id))

      if (allInGroup) {
        const tooltipText = group.roles.map((r) => r.name).join(', ')
        groupRoleIds.forEach((id) => coveredRoleIds.add(id))

        elements.push(
          <Tooltip key={`group-${group.label}`} title={tooltipText}>
            <span
              css={[
                badgeStyles,
                css`
                  background: ${group.colors.bg};
                  color: ${group.colors.fg};
                  cursor: default;
                `,
              ]}
            >
              {group.label}
            </span>
          </Tooltip>
        )
      }
    }

    // Add individual badges for roles not covered by group badges
    for (const role of role_types) {
      if (!coveredRoleIds.has(role.id)) {
        const roleColors = getRoleColors(role.id)
        elements.push(
          <span
            key={`role-${role.id}`}
            css={[
              badgeStyles,
              css`
                background: ${roleColors.bg};
                color: ${roleColors.fg};
              `,
            ]}
          >
            {PLURAL_MAP[role?.name] || role?.name}
          </span>
        )
      }
    }

    return <>{elements}</>
  }, [role_types])

  if (!badges) {
    return null
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px;
        max-width: 100%;
      `}
    >
      <Text
        variant='paragraphXSmall'
        weight='regular'
        color={colors.NEUTRAL_50}
        style='white-space: nowrap; display: none;'
      >
        Visible to:
      </Text>
      {badges}
    </div>
  )
}

interface ProfileLinkProps {
  entityType: 'team' | 'schedule' | 'organization'
  isAppPost: boolean
  avatarSrc: string
  displayName: string
  formattedDate: string
  isOrgOrSchedulePost: boolean
  userProfileSrc: string
  entityName: string
  dateText: string
  navigationUrl?: string
  navigationCursor: string
  handleNavigationClick: (e: React.MouseEvent) => void
  role_types?: {
    id: number
    name: string
  }[]
}

const ProfileLink: React.FC<ProfileLinkProps> = ({
  isAppPost,
  avatarSrc,
  displayName,
  formattedDate,
  isOrgOrSchedulePost,
  userProfileSrc,
  entityName,
  dateText,
  navigationUrl,
  navigationCursor,
  handleNavigationClick,
  role_types = [],
}) => {
  const linkContent = isOrgOrSchedulePost ? (
    <>
      <img src={avatarSrc} alt='Entity logo' css={getAvatarStyles()} />
      <div>
        <div css={profileInfoStyles}>
          <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
            {displayName}
          </Text>
          {isAppPost && <VerifiedIcon />}
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-top: 2px;
          `}
        >
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {formattedDate}
          </Text>
          <RoleBadges role_types={role_types} />
        </div>
      </div>
    </>
  ) : (
    <>
      <div css={avatarContainerStyles}>
        <img src={avatarSrc} alt='Entity logo' css={getAvatarStyles()} />
        <img
          src={userProfileSrc}
          alt='User profile picture'
          css={getUserProfileStyles}
        />
      </div>
      <div>
        <div css={profileInfoStyles}>
          <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
            {entityName}
          </Text>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {dateText}
          </Text>
          <RoleBadges role_types={role_types} />
        </div>
      </div>
    </>
  )

  if (navigationUrl) {
    return (
      <Link
        to={navigationUrl}
        css={[
          profileLinkStyles,
          css`
            cursor: ${navigationCursor};
          `,
        ]}
        onClick={handleNavigationClick}
      >
        {linkContent}
      </Link>
    )
  }

  return (
    <span
      css={[
        profileLinkStyles,
        css`
          cursor: ${navigationCursor};
        `,
      ]}
      onClick={handleNavigationClick}
    >
      {linkContent}
    </span>
  )
}

export default ProfileLink
